import React from 'react';
import {
  Button,
  Typography,
  Box,
  Alert,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { SecaoVendasDrillDownData, SecaoVendasData } from '../../types/secaoVendasDrillDown';
import SecaoVendasDrillDownContent from './SecaoVendasDrillDownContent';
import GenericModal from '../GenericModal';
import EmptyState from '../EmptyState';
import { ProductDrillDownSkeleton } from '../Skeletons/ModalSkeletons';

/**
 * Props do componente - seguindo princípio ISP (Interface Segregation)
 */
interface SecaoVendasDrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: SecaoVendasDrillDownData | null;
  loading: boolean;
  selectedSecao: SecaoVendasData | null;
  error?: string | null;
  onRetry?: () => void;
}

/**
 * Componente para exibir drill down de seções de vendas
 * Princípio SRP: Responsável apenas pela apresentação do modal
 * Princípio OCP: Extensível via props sem modificar código interno
 */
const SecaoVendasDrillDownModal: React.FC<SecaoVendasDrillDownModalProps> = ({
  isOpen,
  onClose,
  data,
  loading,
  selectedSecao,
  error,
  onRetry
}) => {
  /**
   * Constrói título dinâmico do modal
   * Princípio SRP: Responsável apenas pela construção do título
   */
  const getModalTitle = (): string => {
    const baseTitle = '📊 Análise Detalhada da Seção';
    if (selectedSecao?.nome) {
      return `${baseTitle} - ${selectedSecao.nome}`;
    }
    return baseTitle;
  };

  /**
   * Constrói breadcrumb items
   * Princípio SRP: Responsável apenas pela construção do breadcrumb
   */
  const getBreadcrumbItems = () => {
    if (!selectedSecao) return [];
    
    return [
      { 
        label: 'Análises de Vendas', 
        onClick: onClose 
      },
      { 
        label: `Seção: ${selectedSecao.nome}` 
      }
    ];
  };

  /**
   * Constrói ações customizadas do modal
   * Princípio SRP: Responsável apenas pela construção das ações
   */
  const getCustomActions = () => (
    <>
      {error && onRetry && (
        <Button 
          variant="outlined" 
          color="primary"
          onClick={onRetry}
          sx={{ mr: 1 }}
          startIcon={<RefreshIcon />}
        >
          Tentar Novamente
        </Button>
      )}
      <Button onClick={onClose} variant="outlined" color="inherit">
        Fechar
      </Button>
    </>
  );

  /**
   * Renderiza estado de erro
   * Princípio SRP: Responsável apenas pela renderização do estado de erro
   */
  const renderErrorState = () => (
    <Box sx={{ minHeight: '400px', display: 'flex', alignItems: 'center' }}>
      <Alert 
        severity="error" 
        sx={{ width: '100%' }}
        action={
          onRetry && (
            <IconButton
              aria-label="Tentar novamente"
              color="inherit"
              size="small"
              onClick={onRetry}
              title="Tentar novamente"
            >
              <RefreshIcon />
            </IconButton>
          )
        }
      >
        <Typography variant="subtitle1" gutterBottom>
          Erro ao carregar dados da seção
        </Typography>
        <Typography variant="body2">
          {error}
        </Typography>
      </Alert>
    </Box>
  );

  /**
   * Renderiza estado vazio
   * Princípio SRP: Responsável apenas pela renderização do estado vazio
   */
  const renderEmptyState = () => (
    <EmptyState
      title="Nenhum dado disponível"
      description="Não foi possível carregar os detalhes da seção selecionada"
      icon="📊"
      height={400}
    />
  );

  /**
   * Renderiza conteúdo principal com dados
   * Princípio SRP: Responsável apenas pela renderização do conteúdo
   */
  const renderContent = () => {
    if (!data) return null;
    return <SecaoVendasDrillDownContent data={data} />;
  };

  /**
   * Renderiza conteúdo do modal baseado no estado
   * Princípio SRP: Responsável apenas pela lógica de renderização condicional
   */
  const renderModalContent = () => {
    if (error) {
      return renderErrorState();
    }
    
    if (data) {
      return renderContent();
    }
    
    return renderEmptyState();
  };

  return (
    <GenericModal
      open={isOpen}
      onClose={onClose}
      title={getModalTitle()}
      loading={false}
      maxWidth="xl"
      height="90vh"
      showBreadcrumb={true}
      breadcrumbItems={getBreadcrumbItems()}
      actions={getCustomActions()}
    >
      {loading ? (
        <ProductDrillDownSkeleton />
      ) : (
        renderModalContent()
      )}
    </GenericModal>
  );
};

export default SecaoVendasDrillDownModal; 